"use client";

import React, { useState, useEffect, useRef } from "react";
import hclogo from "../../images/hclogo.svg";
import download from "../../images/download.png";
import passTemplate from "../../images/virtualpass.png"; // ✅ virtual pass background
import {
    gradingRules,
    type QuestionRule,
} from "./gradingRules";
import {
    gradeAwareness,
    gradeKnowledge,
    gradeMindset,
    gradeVision,
    type ResponseRecord,
} from "./gradingLogic";

type SectionKey = "Awareness" | "Knowledge" | "Mindset" | "Vision";

const RegisterMultiStep = () => {
    const [sectionIndex, setSectionIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [responses, setResponses] = useState<Record<SectionKey, ResponseRecord>>({
        Awareness: {},
        Knowledge: {},
        Mindset: {},
        Vision: {},
    });
    const [isGradingView, setIsGradingView] = useState(false);
    const [isSubmissionPage, setIsSubmissionPage] = useState(true);
    const [sectionResult, setSectionResult] = useState<{ grade: string; explanation: string } | null>(null);

    const [formData, setFormData] = useState({ fullName: "", email: "" });
    const [userId, setUserId] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // ✅ canvas ref for virtual pass
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const sectionKeys = Object.keys(gradingRules) as SectionKey[];
    const currentSectionKey = sectionKeys[sectionIndex];
    const currentSection = gradingRules[currentSectionKey];
    const questionKeys = Object.keys(currentSection);
    const currentQuestionKey = questionKeys[questionIndex];
    const currentQuestion: QuestionRule = currentSection[currentQuestionKey];

    useEffect(() => {
		window.scrollTo(0, 0)
	}, [])

    const generateAndDownloadPass = async (name: string, id: string) => {
        setIsDownloading(true);
        const canvas = canvasRef.current;
        if (!canvas) {
            setIsDownloading(false);
            return;
        }

        try {
            const bgImage = new Image();
            bgImage.src = passTemplate;

            await new Promise<void>((resolve, reject) => {
                bgImage.onload = () => resolve();
                bgImage.onerror = reject;
            });

            const ctx = canvas.getContext("2d");
            if (!ctx) {
                setIsDownloading(false);
                return;
            }

            // Match canvas to image
            canvas.width = bgImage.width;
            canvas.height = bgImage.height;

            // Draw background
            ctx.drawImage(bgImage, 0, 0);

            // --- CONFIG ---
            ctx.fillStyle = "#3d567a";
            ctx.textBaseline = "top";

            // --- NAME SECTION ---
            const nameX = canvas.width * 0.32;
            const nameY = canvas.height * 0.31;
            const maxNameWidth = canvas.width * 0.5; // allowed width
            let nameFontSize = canvas.width * 0.055;

            ctx.font = `bold ${nameFontSize}px Manrope, sans-serif`;

            // Shrink font size until text fits
            while (ctx.measureText(name).width > maxNameWidth && nameFontSize > 10) {
                nameFontSize -= 1;
                ctx.font = `bold ${nameFontSize}px Manrope, sans-serif`;
            }

            ctx.fillText(name, nameX , nameY);

            // --- ID SECTION ---
            const idX = canvas.width * 0.38;
            const idY = canvas.height * 0.45;
            const maxIdWidth = canvas.width * 0.5;
            let idFontSize = canvas.width * 0.065;

            ctx.font = `bold ${idFontSize}px "Mozilla Headline", sans-serif`;

            // Shrink ID font if needed
            while (ctx.measureText(id).width > maxIdWidth && idFontSize > 10) {
                idFontSize -= 1;
                ctx.font = `bold ${idFontSize}px "Mozilla Headline", sans-serif`;
            }

            ctx.fillText(id, idX , idY);

            // --- DOWNLOAD ---
            canvas.toBlob((blob) => {
                if (!blob) {
                    setIsDownloading(false);
                    return;
                }

                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.download = `haystack-pass-${id}.png`;
                link.href = url;
                link.click();
                URL.revokeObjectURL(url);
                setIsDownloading(false);
            }, "image/png", 1.0);

        } catch (err) {
            console.error("Error generating pass:", err);
            setIsDownloading(false);
        }
    };



    // ----------------------------
    // (Keep all your existing logic below untouched)
    // ----------------------------

    const handleOptionSelect = (option: string) => {
        const multiSelectQuestions = ["Q4", "Q5", "Q15"];
        if (multiSelectQuestions.includes(currentQuestionKey)) {
            setSelectedOptions((prev) =>
                prev.includes(option)
                    ? prev.filter((o) => o !== option)
                    : [...prev, option]
            );
        } else {
            setSelectedOptions([option]);
        }
    };

    const handleNext = () => {
        window.scrollTo(0, 0)
        if (selectedOptions.length === 0) return;
        setResponses((prev) => ({
            ...prev,
            [currentSectionKey]: {
                ...prev[currentSectionKey],
                [currentQuestionKey]:
                    selectedOptions.length > 1 ? selectedOptions : selectedOptions[0],
            },
        }));
        if (questionIndex < questionKeys.length - 1) {
            setQuestionIndex((prev) => prev + 1);
            setSelectedOptions([]);
        } else {
            const sectionResponses = {
                ...responses[currentSectionKey],
                [currentQuestionKey]:
                    selectedOptions.length > 1 ? selectedOptions : selectedOptions[0],
            };
            let result;
            switch (currentSectionKey) {
                case "Awareness":
                    result = gradeAwareness(sectionResponses);
                    break;
                case "Knowledge":
                    result = gradeKnowledge(sectionResponses);
                    break;
                case "Mindset":
                    result = gradeMindset(sectionResponses);
                    break;
                case "Vision":
                    result = gradeVision(sectionResponses);
                    break;
                default:
                    result = { sectionGrade: "Neutral", explanation: "No result available" };
            }
            setSectionResult({
                grade: result.sectionGrade,
                explanation: result.explanation,
            });
            setIsGradingView(true);
        }
    };

    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (
                e.key === "Enter" &&
                !isGradingView &&
                !isSubmissionPage &&
                selectedOptions.length > 0
            ) {
                e.preventDefault();
                handleNext();
            }
        };
        window.addEventListener("keydown", handleKeyPress);
        return () => window.removeEventListener("keydown", handleKeyPress);
    }, [selectedOptions, isGradingView, isSubmissionPage]);

    const handleBack = () => {
        window.scrollTo(0, 0)
        if (questionIndex > 0) {
            setQuestionIndex((prev) => prev - 1);
            setSelectedOptions([]);
        }
    };

    const handleContinueToNextSection = () => {
        window.scrollTo(0, 0)
        if (sectionIndex < sectionKeys.length - 1) {
            setSectionIndex((prev) => prev + 1);
            setQuestionIndex(0);
            setIsGradingView(false);
            setSectionResult(null);
            setSelectedOptions([]);
        } else {
            setIsGradingView(false);
            setIsSubmissionPage(true);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmitSurvey = async () => {
        window.scrollTo(0, 0)
        setIsSubmitting(true);
        try {
            const res = await fetch("https://thenewbrook.com/api/haystack/submit_survey.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    fullName: formData.fullName,
                    email: formData.email,
                    responses,
                }),
            });
            const data = await res.json();
            if (data.success) {
                setUserId(data.userId || Math.floor(100000 + Math.random() * 900000).toString());
                setIsSubmitted(true);
            } else {
                alert("⚠️ There was an issue submitting your survey.");
            }
        } catch (err) {
            console.error(err);
            alert("❌ Failed to connect to the server.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // ✅ Success Page (added generateAndDownloadPass on button)
    return (
        <div className="flex justify-center md:py-12 md:px-12 lg:px-36 items-center min-h-screen bg-[#161515]">
            <div className="flex slide flex-col items-start md:rounded-2xl shadow-xl py-10 md:py-20 px-7 md:px-10">
                <img src={hclogo} alt="hclogo" className="md:h-12" />

                {/* Hidden canvas for virtual pass generation */}
                <canvas ref={canvasRef} style={{ display: "none" }} />

                {/* STEP 2: Actual survey questions */}
                {!isGradingView && !isSubmissionPage && (
                    <>
                        {currentQuestion?.options && (
                            <h1 className="py-4 sm:py-12 px-4 text-white text-xl sm:2xl lg:text-3xl font-semibold creato">
                                {currentQuestion.question}
                            </h1>
                        )}

                        <div className="card border rounded-3xl w-full my-4 text-white border-[#A9A9A95C] py-6 md:py-9 px-4 md:px-10">
                            <div className="flex justify-center gap-3 mb-6">
                                {questionKeys.map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`h-1 w-8 rounded-full ${idx <= questionIndex ? "bg-orange-500" : "bg-gray-600"
                                            }`}
                                    />
                                ))}
                            </div>

                            {currentQuestion?.options && (
                                <>
                                    <h2 className="text-lg md:text-2xl mb-8 text-white creato">
                                        {["Q4", "Q5", "Q15"].includes(currentQuestionKey)
                                            ? "You can choose multiple options"
                                            : "Choose the category you belong to"}
                                    </h2>

                                    <div className="space-y-3 md:space-y-6">
                                        {Object.keys(currentQuestion.options).map((option, index) => {
                                            const isSelected = selectedOptions.includes(option);
                                            return (
                                                <button
                                                    key={index}
                                                    onClick={() => handleOptionSelect(option)}
                                                    className={`text-sm text-left rounded-xl py-4 px-5 transition-all w-full ${isSelected
                                                            ? "bg-[#FF6A00] text-white"
                                                            : "bg-[#3a3a3a] text-[#e0e0e0] hover:bg-[#424242]"
                                                        }`}
                                                >
                                                    <span className="text-sm md:text-lg leading-relaxed">
                                                        {option}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            <div className="text-xs flex justify-end gap-x-8 my-10 font-bold">
                                <button
                                    onClick={handleBack}
                                    disabled={questionIndex === 0}
                                    className={`transition-all ${questionIndex === 0 ? "text-[#444]" : "text-[#626262] hover:text-white"
                                        }`}
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleNext}
                                    className="transition-all bg-gradient-to-b from-[#5A5A5A] to-[#BBBBBB] text-white px-6 py-3 rounded-xl"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {isGradingView && (
                    <div className="text-center my-10 text-white">
                        <h2 className="text-2xl font-semibold mb-4">
                            You are mostly{" "}
                            <span className="text-orange-400">{sectionResult?.grade}</span>
                        </h2>
                        <p className="text-gray-300 text-base mb-6">{sectionResult?.explanation}</p>
                        <button
                            onClick={handleContinueToNextSection}
                            className="transition-all bg-gradient-to-b from-[#5A5A5A] to-[#BBBBBB] text-white px-6 py-3 rounded-xl mt-6"
                        >
                            Continue
                        </button>
                    </div>
                )}

                {isSubmissionPage && (
                    <div className="w-full text-white text-center my-10">
                        {!isSubmitted ? (
                            <>
                                {/* registration form remains same */}
                                <h2 className="text-2xl font-semibold mb-6 text-left max-w-3xl ">
                                    Would you love to learn from missionaries — hearing about their exploits and experiences across different localities and cultures?
                                </h2>
                                <p className="text-gray-400 mb-8">
                                    Please enter your details to register.
                                </p>
                                <div className="flex flex-col items-center gap-4">
                                    <input
                                        type="text"
                                        name="fullName"
                                        placeholder="Full Name"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        className="w-full max-w-md px-4 py-3 rounded-xl bg-[#3a3a3a] text-white outline-none"
                                    />
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Email Address"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full max-w-md px-4 py-3 rounded-xl bg-[#3a3a3a] text-white outline-none"
                                    />
                                </div>

                                <div className="flex flex-col items-center gap-4">
                                    <button
                                        onClick={handleSubmitSurvey}
                                        disabled={isSubmitting}
                                        className="transition-all bg-gradient-to-b from-[#5A5A5A] to-[#BBBBBB] text-white px-6 py-3 rounded-xl mt-6 disabled:opacity-50"
                                    >
                                        {isSubmitting ? "Submitting..." : "Register"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="my-4 h-[100vh]">
                                <h2 className="text-3xl font-semibold text-white mb-4">
                                    Congratulations!
                                </h2>
                                <p className="font-light creato text-[#D6D6D6]">
                                    You have successfully completed the assessment.
                                </p>
                                <img src={download} alt="" className="h-[320px]" />
                                <p className="font-light creato text-[#D6D6D6]">
                                    Click here to download your Virtual Pass.
                                </p>

                                <div className="flex flex-col items-center gap-4">
                                    <button
                                        onClick={() =>
                                            generateAndDownloadPass(
                                                formData.fullName,
                                                userId || "000000"
                                            )
                                        }
                                        disabled={isDownloading}
                                        className="transition-all bg-gradient-to-b from-[#5A5A5A] to-[#BBBBBB] text-white px-6 py-3 rounded-xl mt-6 ">
                                        {isDownloading? "Downloading..." : "Downlaod" }
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegisterMultiStep;
